import React, { useState, useCallback } from 'react';
import { useWeb3 } from '../context/Web3Context';
import {
  PLAY_VAULT_ABI,
  USDT_ABI,
  PLAY_VAULT_ADDRESS,
  USDT_CONTRACT_ADDRESS,
} from '../config/playVault';
import './PlayVaultPanel.css';

const weiToUsd = (wei) => {
  if (!wei || wei === '0') return '0.00';
  try { return (Number(BigInt(wei)) / 1e18).toFixed(2); } catch { return '0.00'; }
};

const usdToWei = (usd) => {
  try {
    const val = parseFloat(usd);
    if (isNaN(val) || val <= 0) return null;
    return BigInt(Math.round(val * 1e18)).toString();
  } catch { return null; }
};

const PlayVaultPanel = ({ vaultBalance, platformEarnings, onRefresh }) => {
  const { web3, account } = useWeb3();
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [loading, setLoading] = useState('');
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const setStatus = (ok, text) => {
    if (ok) { setMsg(text); setError(''); }
    else { setError(text); setMsg(''); }
  };

  const handleDeposit = useCallback(async () => {
    setError(''); setMsg('');
    const amtWei = usdToWei(depositAmount);
    if (!amtWei) { setStatus(false, 'Enter a valid amount'); return; }
    if (!PLAY_VAULT_ADDRESS || !USDT_CONTRACT_ADDRESS) {
      setStatus(false, 'Vault not deployed yet — set REACT_APP_PLAY_VAULT_ADDRESS'); return;
    }
    try {
      setLoading('deposit');
      const usdt = new web3.eth.Contract(USDT_ABI, USDT_CONTRACT_ADDRESS);

      // Check allowance first
      const allowance = await usdt.methods.allowance(account, PLAY_VAULT_ADDRESS).call();
      if (BigInt(allowance) < BigInt(amtWei)) {
        setMsg('Approving USDT…');
        await usdt.methods.approve(PLAY_VAULT_ADDRESS, amtWei).send({ from: account });
      }

      setMsg('Depositing…');
      const vault = new web3.eth.Contract(PLAY_VAULT_ABI, PLAY_VAULT_ADDRESS);
      await vault.methods.deposit(amtWei).send({ from: account });

      setDepositAmount('');
      setStatus(true, `Deposited $${depositAmount} USDT to vault`);
      onRefresh();
    } catch (e) {
      setStatus(false, e?.message?.split('\n')[0] || 'Deposit failed');
    } finally {
      setLoading('');
    }
  }, [web3, account, depositAmount, onRefresh]);

  const handleWithdraw = useCallback(async () => {
    setError(''); setMsg('');
    const amtWei = usdToWei(withdrawAmount);
    if (!amtWei) { setStatus(false, 'Enter a valid amount'); return; }
    if (!PLAY_VAULT_ADDRESS) {
      setStatus(false, 'Vault not deployed yet — set REACT_APP_PLAY_VAULT_ADDRESS'); return;
    }
    if (BigInt(amtWei) > BigInt(vaultBalance || '0')) {
      setStatus(false, 'Amount exceeds vault balance'); return;
    }
    try {
      setLoading('withdraw');
      setMsg('Withdrawing…');
      const vault = new web3.eth.Contract(PLAY_VAULT_ABI, PLAY_VAULT_ADDRESS);
      await vault.methods.withdraw(amtWei).send({ from: account });

      setWithdrawAmount('');
      setStatus(true, `Withdrew $${withdrawAmount} USDT to wallet`);
      onRefresh();
    } catch (e) {
      setStatus(false, e?.message?.split('\n')[0] || 'Withdrawal failed');
    } finally {
      setLoading('');
    }
  }, [web3, account, withdrawAmount, vaultBalance, onRefresh]);

  return (
    <div className="vault-panel">
      <div className="vault-stats">
        <div className="vault-stat">
          <span className="vault-stat-label">Your Vault Balance</span>
          <span className="vault-stat-value">${weiToUsd(vaultBalance)} USDT</span>
        </div>
      </div>

      <div className="vault-actions">
        <div className="vault-action-group">
          <label className="vault-label">Deposit USDT</label>
          <div className="vault-row">
            <input
              className="vault-input"
              type="number"
              min="0.01"
              step="0.01"
              placeholder="Amount (USDT)"
              value={depositAmount}
              onChange={e => setDepositAmount(e.target.value)}
              disabled={!!loading}
            />
            <button
              className="vault-btn vault-btn-deposit"
              onClick={handleDeposit}
              disabled={!!loading || !depositAmount}
            >
              {loading === 'deposit' ? '…' : 'Deposit'}
            </button>
          </div>
        </div>

        <div className="vault-action-group">
          <label className="vault-label">Withdraw USDT</label>
          <div className="vault-row">
            <input
              className="vault-input"
              type="number"
              min="0.01"
              step="0.01"
              placeholder="Amount (USDT)"
              value={withdrawAmount}
              onChange={e => setWithdrawAmount(e.target.value)}
              disabled={!!loading}
            />
            <button
              className="vault-btn vault-btn-withdraw"
              onClick={handleWithdraw}
              disabled={!!loading || !withdrawAmount}
            >
              {loading === 'withdraw' ? '…' : 'Withdraw'}
            </button>
          </div>
        </div>
      </div>

      {msg && <p className="vault-msg vault-msg-ok">{msg}</p>}
      {error && <p className="vault-msg vault-msg-err">{error}</p>}
    </div>
  );
};

export default PlayVaultPanel;
